import ColoredChipsCell from "@common/components/coloredChipsCell/ColoredChipsCell";
import { DotLegalButton, DotLegalOverflowMenu, DotLegalTooltip, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../../localization/useTranslation";
import { Box, TableCell, TableRow } from "@mui/material";
import DocumentPreviewLink from "../../../../common/components/documentPreview/DocumentPreviewLink";
import { ContractDocumentRow, ContractDocumentTableModel } from "./ContractDocuments.types";
import { DocumentStatus } from "../../../../documents/Documents.types";
import { useContractDocumentHooks } from "./ContractDocuments.hooks";
import AddEditContractDocumentDialog from "../addEditContractDocumentDialog/AddEditContractDocumentDialog";
import { DeleteContractDocumentDialog } from "../deleteContractDocumentDialog/DeleteContractDocumentDialog";
import { useUserContext } from "../../../../auth/userContextProvider/UserContextProvider";
import { pinnedCommentIcon, PurpleLockIcon } from "../../../../common/icons";
import DocumentAccessCell from "../../../../common/components/documentAccessCell/DocumentAccessCell";
import { isNullOrWhitespace } from "../../../../common/stringOperations";
import { containsHtmlTags } from "@common/utilities";
import ToggleDocumentAsMainDialog from "./toggleDocumentAsMainDialog/ToggleDocumentAsMainDialog.tsx";
import DocumentsEmptyState from "@common/components/documentsEmptyState/DocumentsEmptyState.tsx";
import AddDocumentsEmptyState from "@common/components/documentsEmptyState/AddDocumentsEmptyState.tsx";
import { usePlanContext } from "../../../../auth/planProvider/PlanProvider";
import { useDocumentPreviewManager } from "../../../../common/components/documentPreview/useDocumentPreviewManager";
import { EntityType } from "../../../../common/Common.types";
import AuditLogDialog from "../../../../auditlogDialog/AuditLogDialog.tsx";
import BestPracticeCheckDialog from "../../../../common/components/bestPracticeCheckDialog/BestPracticeCheckDialog";
import BestPracticeCheckResultDialog from "@common/components/bestPracticeCheckResultDialog/BestPracticeCheckResultDialog.tsx";
import BestPracticeCoverageResult from "@common/components/bestPracticeCheckResultDialog/BestPracticeCoverageResult.tsx";
import { useBestPracticeDialogsHook } from "@common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.hooks.ts";
import TableHeaderLock from "../../../../common/components/TableHeaderLock";
import { getAiQualityScanOverflowMenuItem } from "@common/components/bestPracticeCheckDialog/aiQualityScanOverflowMenuItem.ts";
import { getContractAiUpdateOverflowMenuItem } from "../../../contractAiUpdate/contractAiUpdateMenuItem.ts";
import { getCreateContractWithAiOverflowMenuItem } from "../../../addContractButton/createContractPane/createContractWithAiMenuItem.ts";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import { DownloadAllDocumentsButton } from "@common/components/downloadAllDocuments/DownloadAllDocumentsButton";
import { useAiFeatureAccess } from "../../../../plan/aiFeatureGate/useAiFeatureAccess";
import MentionInputRender from "@common/components/mentionInput/MentionInputRender";

export interface IContractDocuments {
    contractId: string;
    contractName?: string;
    onUpdate?: () => void;
    emptyStateWrappedInPaper: boolean;
    errorText?: string;
    /** Switches the contract to its obligations tab. Only the tab owner can do this, so it comes from above. */
    onNavigateToObligations?: () => void;
    /** Set when the user's role makes the contract's Documents field read-only or hides it. */
    readOnly?: boolean;
}

function ContractDocuments(props: IContractDocuments) {
    const { translateString, translateDate } = useTranslation();
    const { permissions } = useUserContext();
    const { platformFeatures } = usePlanContext();
    const { showAiQualityAssurance, planActivated } = useAiFeatureAccess();
    const {
        documents,
        hasDocuments,
        isLoading,
        setDocumentForAddOrEdit,
        documentForAddOrEdit,
        setDocumentForDeletion,
        documentForDeletion,
        refetchDocuments,
        downloadingDocument,
        documentToSetAsMainDocument,
        setDocumentToSetAsMainDocument,
        onDownloadClick,
        showAuditLogDialog,
        setShowAuditLogDialog,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        getAiUpdateBlockedTranslationKey,
        getAiDisabledTranslationKey,
        getAiDisabledTooltip,
        canStartAiUpdateFromDocument,
        startAiUpdateFromDocument,
        canCreateContractFromDocument,
        startCreateContractFromDocument,
        getCreateContractFromDocumentBlockedTooltip,
        canEditDocuments,
    } = useContractDocumentHooks(props);

    const {
        bestPracticeCheckDocument,
        setBestPracticeCheckDocument,
        bestPracticeCheckDocumentToQualityAssurance,
        setBestPracticeCheckDocumentToQualityAssurance,
        bestPracticeCheckInitialResult,
        setBestPracticeCheckInitialResult,
        onCheckComplete,
    } = useBestPracticeDialogsHook();

    // Previews open on the stage, where the document pane offers the AI review in the rail beside it.
    const { previewableDocuments, handleDocumentClick, getPreviewHref } = useDocumentPreviewManager({
        documents: documents,
        entityType: EntityType.Contract,
        entityId: props.contractId,
        entityTitle: props.contractName,
        documentEntry: { offerObligationExtraction: { contractId: props.contractId, onGoToObligations: props.onNavigateToObligations } },
    });

    const isDocumentPreviewable = (documentId: string) => {
        return previewableDocuments.some((d) => d.documentId === documentId);
    };

    function GetDocumentHeader() {
        const headers = Array<ITableHeader<ContractDocumentTableModel>>();
        headers.push({ property: "name", text: translateString("name") });
        headers.push({ property: "documentTypeString", text: translateString("type") });
        headers.push({ property: "status", text: translateString("status") });
        headers.push({ property: "ownerString", text: translateString("owner") });

        if (platformFeatures.tags && permissions.canAccessTags) {
            headers.push({ property: "tagsString", text: translateString("tags") });
        }
        headers.push({ property: "version", text: translateString("documentVersion") });
        headers.push({ property: "created", text: translateString("created") });
        headers.push({ property: "dataId", text: translateString("accessDocument") });

        if (showAiQualityAssurance) {
            headers.push({
                property: "aiQualityAssuranceResult",
                text: translateString("aiQualityScan"),
                extraHeaderContent: !planActivated ? <TableHeaderLock tooltip={translateString("aiAddOnPurchaseTooltip")} /> : undefined,
            });
        }

        headers.push({ property: "comment", text: translateString("comment") });

        return headers;
    }

    function getMenuItems(document: ContractDocumentRow) {
        let menuItems = [];

        if (canEditDocuments) {
            menuItems.push({
                menuItem: translateString("editDocument"),
                onClick: () => {
                    setDocumentForAddOrEdit({ show: true, selectedDocument: document });
                },
            });
        }

        menuItems.push({
            menuItem: translateString("downloadDocument"),
            onClick: () => {
                onDownloadClick(document.documentId, document.dataId!, document.fileName!, document.link);
            },
        });

        if (canEditDocuments) {
            menuItems.push({
                menuItem: translateString("deleteDocument"),
                onClick: () => {
                    setDocumentForDeletion(document);
                },
            });

            menuItems.push({
                menuItem: document.isMainDocument ? translateString("removeAsMainDocument") : translateString("setAsMainDocument"),
                onClick: () => {
                    setDocumentToSetAsMainDocument(document);
                },
            });
        }

        if (canStartAiUpdateFromDocument(document)) {
            menuItems.push(
                getContractAiUpdateOverflowMenuItem(
                    () => startAiUpdateFromDocument(document),
                    getAiUpdateBlockedTranslationKey(),
                    translateString,
                    getAiDisabledTranslationKey(document)
                )
            );
        }

        if (canCreateContractFromDocument(document)) {
            menuItems.push(
                getCreateContractWithAiOverflowMenuItem(
                    () => startCreateContractFromDocument(document),
                    getCreateContractFromDocumentBlockedTooltip(),
                    translateString,
                    getAiDisabledTooltip(document)
                )
            );
        }

        if (showAiQualityAssurance && !isNullOrWhitespace(document.fileName)) {
            menuItems.push(
                getAiQualityScanOverflowMenuItem(
                    () => {
                        if (document.aiQualityAssuranceResult) {
                            setBestPracticeCheckDocument({ documentId: document.documentId, documentVersionId: document.documentVersionId });
                        } else {
                            setBestPracticeCheckDocumentToQualityAssurance({ documentId: document.documentId, documentVersionId: document.documentVersionId });
                        }
                    },
                    planActivated,
                    document.isTooLargeForAi,
                    translateString
                )
            );
        }

        if (permissions.canAccessAuditLog) {
            menuItems.push({
                menuItem: translateString("auditlog"),
                onClick: () => {
                    setShowAuditLogDialog(document.contractDocumentId);
                },
                planMenuItemInfo: {
                    hasAccess: platformFeatures.auditLog,
                    menuName: translateString("auditlog"),
                    tooltip: translateString("upgradePrivacyPlanToday"),
                },
            });
        }

        return menuItems;
    }

    return (
        <>
            {hasDocuments ? (
                <DotLegalTableWithFilterBar
                    hasOverflowMenu
                    filterBar={{
                        isLoading: isLoading,
                        filterData: getFilterOptions(),
                        fetchFilterData: true,
                        onMenuClose: onFilterChange,
                        onRemove: onFilterChange,
                        pinnedFilterIds: pinnedFilterIds,
                        onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                    }}
                    headers={GetDocumentHeader()}
                    rowsPerPage={30}
                    defaultOrderBy={"created"}
                    defaultOrder={"desc"}
                    pinnedProperty={"isMainDocument"}
                    isLoading={isLoading}
                    data={convertRowToTableViewModel(documents)}
                    clickableRows={permissions.contractManagementPermissions.permissions.edit}
                    emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noDocumentsAttachedToLegalEntity")}
                    renderRow={(row, i) => {
                        const previewHref = getPreviewHref(row);
                        return (
                            <TableRow
                                key={row.documentId}
                                onClick={() => handleDocumentClick(row)}
                                sx={{ backgroundColor: row.isMainDocument ? "#fafaff" : "inherit" }}
                            >
                                <TableCell>
                                    <Box
                                        sx={(theme) => ({
                                            display: "flex",
                                            gap: 1,
                                            alignItems: "center",
                                            color: theme.palette.primary.main,
                                            fontWeight: theme.typography.fontWeightBold,
                                        })}
                                    >
                                        <DocumentPreviewLink previewHref={previewHref} onPreviewClick={() => handleDocumentClick(row)}>
                                            {row.name}
                                        </DocumentPreviewLink>{" "}
                                        {row.isMainDocument ? (
                                            <DotLegalTooltip text={translateString("documentIsMarkedAsMainDocument")}>{pinnedCommentIcon}</DotLegalTooltip>
                                        ) : undefined}
                                        {row.groupEntityAccessIds.length > 0 ? (
                                            <DotLegalTooltip text={translateString("contractDocumentGroupEntityRestriction")}>{PurpleLockIcon}</DotLegalTooltip>
                                        ) : undefined}
                                    </Box>
                                </TableCell>
                                <TableCell>{row.documentTypeString}</TableCell>
                                <TableCell>{row.statusString}</TableCell>
                                <TableCell>{row.ownerString}</TableCell>
                                {platformFeatures.tags && permissions.canAccessTags && (
                                    <TableCell>
                                        <ColoredChipsCell items={row.tags ?? []} />
                                    </TableCell>
                                )}
                                <TableCell>{row.version}</TableCell>
                                <TableCell>{row.createdString}</TableCell>
                                <DocumentAccessCell
                                    documentId={row.documentId}
                                    link={row.link}
                                    downloadingDocument={downloadingDocument}
                                    isPreviewable={isDocumentPreviewable(row.documentId)}
                                    previewHref={previewHref}
                                    onDownloadClick={() => onDownloadClick(row.documentId, row.dataId!, row.fileName!, row.link)}
                                    onPreviewClick={() => handleDocumentClick(row)}
                                />
                                {showAiQualityAssurance && (
                                    <TableCell>
                                        {!isNullOrWhitespace(row.fileName) && (
                                            <BestPracticeCoverageResult
                                                result={row.aiQualityAssuranceResult}
                                                isTooLargeForAi={row.isTooLargeForAi}
                                                onClick={() => {
                                                    if (row.aiQualityAssuranceResult) {
                                                        setBestPracticeCheckDocument({
                                                            documentId: row.documentId,
                                                            documentVersionId: row.documentVersionId,
                                                        });
                                                    } else {
                                                        setBestPracticeCheckDocumentToQualityAssurance({
                                                            documentId: row.documentId,
                                                            documentVersionId: row.documentVersionId,
                                                        });
                                                    }
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                )}
                                {/* A rich text editor per row only where the comment is rich text; most are plain. The row
                                    opens the preview, so a click on a mention must not open it too. */}
                                {containsHtmlTags(row.comment ?? "") ? (
                                    <TableCell onClick={(event) => event.stopPropagation()}>
                                        <MentionInputRender value={row.comment} />
                                    </TableCell>
                                ) : (
                                    <TableCell sx={{ whiteSpace: "pre-wrap" }}>{row.comment}</TableCell>
                                )}
                                <TableCell align={"right"}>
                                    {permissions.contractManagementPermissions.permissions.edit && <DotLegalOverflowMenu items={getMenuItems(row)} />}
                                </TableCell>
                            </TableRow>
                        );
                    }}
                />
            ) : props.emptyStateWrappedInPaper ? (
                <AddDocumentsEmptyState
                    canAdd={canEditDocuments}
                    onAddClick={() => setDocumentForAddOrEdit({ show: true, selectedDocument: undefined })}
                    errorText={props.errorText}
                />
            ) : (
                <DocumentsEmptyState errorText={props.errorText} />
            )}
            {documentForAddOrEdit.show && (
                <AddEditContractDocumentDialog
                    onDialogClose={() => {
                        setDocumentForAddOrEdit({ show: false, selectedDocument: undefined });
                        refetchDocuments();

                        if (props.onUpdate) {
                            props.onUpdate();
                        }
                    }}
                    isEdit={documentForAddOrEdit.selectedDocument !== undefined}
                    selectedDocument={documentForAddOrEdit.selectedDocument}
                    contractId={props.contractId}
                    contractName={props.contractName}
                />
            )}
            {documentForDeletion && (
                <DeleteContractDocumentDialog
                    onCloseDialog={() => setDocumentForDeletion(undefined)}
                    onDeleted={() => {
                        refetchDocuments();
                        setDocumentForDeletion(undefined);

                        if (props.onUpdate) {
                            props.onUpdate();
                        }
                    }}
                    selectedDocument={documentForDeletion}
                    contractId={props.contractId}
                />
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2, gap: 2 }}>
                <DownloadAllDocumentsButton
                    entityType={EntityType.Contract}
                    entityId={props.contractId}
                    show={documents.length > 1 || documents.some((x) => x.version > 1)}
                />
                {canEditDocuments && (
                    <DotLegalButton
                        buttonType={"primary"}
                        onClick={() => {
                            setDocumentForAddOrEdit({ show: true, selectedDocument: undefined });
                        }}
                    >
                        {translateString("addDocument")}
                    </DotLegalButton>
                )}
            </Box>
            {documentToSetAsMainDocument && (
                <ToggleDocumentAsMainDialog
                    contractId={props.contractId}
                    currentMainDocumentId={documents.find((x) => x.isMainDocument)?.contractDocumentId}
                    document={documentToSetAsMainDocument}
                    onCloseDialog={() => setDocumentToSetAsMainDocument(undefined)}
                    onDocumentToggledAsMainDocument={() => {
                        refetchDocuments();
                        setDocumentToSetAsMainDocument(undefined);
                    }}
                />
            )}
            {!isNullOrWhitespace(showAuditLogDialog) && (
                <AuditLogDialog
                    entityType={EntityType.ContractDocument}
                    entityId={showAuditLogDialog!}
                    entityName={documents.find((x) => x.contractDocumentId === showAuditLogDialog)?.name ?? ""}
                    onDialogClose={() => setShowAuditLogDialog(null)}
                />
            )}

            {bestPracticeCheckDocumentToQualityAssurance && (
                <BestPracticeCheckDialog
                    documentId={bestPracticeCheckDocumentToQualityAssurance.documentId}
                    onDialogClose={() => {
                        setBestPracticeCheckDocumentToQualityAssurance(undefined);
                        refetchDocuments();
                    }}
                    onCheckComplete={onCheckComplete}
                />
            )}

            {bestPracticeCheckDocument && (
                <BestPracticeCheckResultDialog
                    documentId={bestPracticeCheckDocument.documentId}
                    initialResult={bestPracticeCheckInitialResult}
                    onDialogClose={() => {
                        setBestPracticeCheckDocument(undefined);
                        setBestPracticeCheckInitialResult(undefined);
                        refetchDocuments();
                    }}
                />
            )}
        </>
    );

    function convertRowToTableViewModel(data: Array<ContractDocumentRow> | undefined): Array<ContractDocumentTableModel> {
        return (
            data?.map((x) => {
                return {
                    ...x,
                    ownerString: x.owner.name,
                    documentTypeString: x.documentType.name,
                    statusString: translateString(DocumentStatus[x.status].toLowerCase()),
                    createdString: translateDate(x.created),
                    tagsString: x.tags.map((tag) => tag.name).join(", ") ?? "",
                };
            }) ?? []
        );
    }
}

export default ContractDocuments;
